import { Box, BoxProps } from "@chakra-ui/layout";
import { useDrag, useDrop } from "react-dnd";
import { BsCollection as BankIcon } from 'react-icons/bs';
import Mspr from "../utils/Mspr";
import { Bank, Preset } from "../utils/types";
import { DraggableType, DropZone } from "./DropZone";
import { Title } from "./Title";

interface Props extends BoxProps {
    bank: Bank;
    index: number;
    hideIndices: boolean;
    handleRename: (entity: Bank | Preset, newName: string) => void;
    handlePresetDrop: (item: Preset, targetBankId: string, targetIndex: number) => void;
    onDragStart: () => void;
    onDragEnd: () => void;
    onBankSelect: () => void;
    isDragging: boolean;
    isActive: boolean;
}

export const BankCard = ({
    bank,
    index: bankIndex,
    hideIndices,
    handleRename,
    handlePresetDrop,
    onDragStart,
    onDragEnd,
    onBankSelect,
    isDragging,
    isActive,
    ...boxProps
}: Props) => {
    const isEmpty = Mspr.isEmpty(bank);
    const isFull = Mspr.isFull(bank);
    const [{ opacity }, drag] = useDrag({
        item: bank,
        type: DraggableType.BANK,
        collect: (monitor) => ({
            opacity: (monitor.isDragging() || isEmpty) ? 0.5 : 1,
        }),
        previewOptions: {
            captureDraggingState: true,
        },
    });

    const [{ isOver, canDrop }] = useDrop({
        accept: DraggableType.PRESET,
        drop: (item: Preset) => handlePresetDrop(item, bank.uniqueId, Mspr.findEmptySlot(bank)),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            // is not full and is not self
            canDrop: monitor.canDrop() && !isFull && monitor.getItem().uniqueId !== bank.uniqueId,
        }),
    });


    return <Box
        ref={drag}
        touchaction='none'
        transform={canDrop && isOver ? 'scale(1.05)' : 'scale(1)'}
        transition='transform 0.2s'
        opacity={opacity}
        borderRadius="md"
        padding="1rem"
        display='grid'
        gridTemplateColumns='1fr 14fr'
        gridTemplateRows='auto'
        alignItems='center'
        justifyContent='start'
        cursor='pointer'
        style={{ opacity }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onBankSelect}
        bg={isActive ? '#F99302' : '#fff'}
        _hover={{
            filter: 'brightness(0.9)',
        }}
        {...boxProps}
    >
        <DropZone
            accept={[DraggableType.PRESET]}
            // when hovering over for 2 seconds, call onBankSelect
            onDrop={(item: Preset) => {
                // drop into the end of the bank
                const lastPresetIndex = Mspr.findEmptySlot(bank);
                // if same bank, do nothing
                if (Mspr.containsPreset(bank, item)) return;
                onDragEnd();
                handlePresetDrop(item, bank.uniqueId, lastPresetIndex);
            }}
            index={-1}
            // primary color
            bgColor='#F99302'
            height='100%'
            width='100%'
            maxHeight='100%'
            position='absolute'
            zIndex={-1}
        />
        <BankIcon />
        <Title
            entity={bank}
            index={bankIndex}
            hideIndices={hideIndices}
            handleRename={handleRename}
            alignItems='center'
            zIndex={isDragging ? -100 : 0}
        />
    </Box>;
}
